import { Tab } from "@headlessui/react";
import { ChevronLeftIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { useFetcher } from "@remix-run/react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import PinField, { usePinField } from "react-pin-field";
import { twJoin } from "tailwind-merge";
import Modal from "./Modal";

type Props = {
  intent?: string;
  showPinView?: boolean;
  className?: string;
  open?: boolean;
  onAuth(): void;
  onClose(): void;
};

type ActionResponse =
  | { error: string }
  | { code: string }
  | { auth: true }
  | {};

export default function LoginForm(props: Props) {
  const [email, setEmail] = useState<string>();
  const formRef = useRef<HTMLFormElement>(null);
  const [showPin, setShowPin] = useState(false);
  const fetcher = useFetcher<ActionResponse>();
  const pin = usePinField();
  const actionData = fetcher.data;
  const updateEmail = props.intent === "update-email";

  const submit = useCallback(() => {
    fetcher.submit(formRef.current, {
      action: "/account/login",
      method: "POST",
      preventScrollReset: true,
    });
  }, [formRef]);

  useEffect(() => {
    if (!actionData) return;
    if ("error" in actionData) {
      toast.error(actionData.error, { id: "auth" });
      return;
    }
    if ("code" in actionData) {
      const emailInput = formRef.current?.elements.namedItem("email");
      if (emailInput instanceof HTMLInputElement) {
        setEmail(emailInput.value);
      }
      toast.success("We've sent you an access code by email.", {
        id: "auth",
      });
      pin.refs.current?.[0]?.focus();
      setShowPin(true);
      return;
    }
    if ("auth" in actionData) props.onAuth();
    toast.dismiss("auth");
  }, [actionData]);

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      ref={formRef}
      action="/account/login"
      method="POST"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h2>
        {updateEmail ? "Update your email address" : "Access your account"}
      </h2>
      <input type="hidden" name="intent" defaultValue={props.intent} />
      {showPin ? (
        <div className="space-y-4">
          <p className="grid gap-2 text-xs">
            You should have received an email from us with your unique access
            code. The code is valid for the next 5 minutes. Please check your
            inbox.
            <button
              type="button"
              className="mx-auto block underline outline-none"
              onClick={() => {
                const data = new FormData();
                if (!email) return;
                data.append("email", email);
                if (props.intent) data.append("intent", props.intent);
                fetcher.submit(data, {
                  action: "/account/login",
                  method: "POST",
                  preventScrollReset: true,
                });
                pin.setValue("");
              }}
            >
              Resend code
            </button>
          </p>
          <label className="grid grid-cols-5 gap-2">
            <span className="col-span-5">Enter pin code</span>
            <PinField
              type="number"
              handler={pin}
              className="no-step text-center"
              autoFocus
              length={5}
              name="code"
              onComplete={submit}
              required
            />
          </label>
          <footer className="flex flex-col place-items-center gap-10">
            <button className="button mx-0 flex w-full items-center justify-center gap-4 rounded-lg bg-black">
              <span className="font-light uppercase tracking-widest">
                Verify
              </span>
              <LockClosedIcon className="-mr-4 size-4" />
            </button>
            <button
              type="button"
              className="flex items-center gap-2 text-sm"
              onClick={() => setShowPin(false)}
            >
              <ChevronLeftIcon className="-ml-8 size-4" />
              <span>Back</span>
            </button>
          </footer>
        </div>
      ) : (
        <>
          <p className="mb-4 tracking-wide">
            Set-up projects, create and save moodboards to store all of your
            ideas in one place.
          </p>
          <Tab.Group>
            <Tab.List
              className={twJoin(
                "grid w-full grid-cols-2 gap-1 rounded bg-neutral-100 p-1",
                "[&_button]:p-2",
                "[&_button]:font-semibold",
                "[&_button]:text-sm",
                "[&_button]:rounded",
                updateEmail && "hidden",
              )}
            >
              {["Sign-in", "Register"].map((k) => (
                <Tab
                  key={k}
                  className={({ selected }) =>
                    twJoin(selected && "bg-[#C57D54] text-white outline-none")
                  }
                >
                  {k}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels className="w-full">
              <Tab.Panel>
                <label>
                  <span>{updateEmail ? "New" : ""} Email address</span>
                  <input type="email" name="email" required autoFocus />
                </label>
              </Tab.Panel>
              <Tab.Panel className="space-y-4">
                <label>
                  <span>Full name*</span>
                  <input name="name" required />
                </label>
                <label>
                  <span>Email address*</span>
                  <input type="email" name="email" required />
                </label>
                <label>
                  <span>Sector*</span>
                  <select name="sector" required>
                    <option value="private">House Builder</option>
                    <option value="social">Social Housing Provider</option>
                    <option value="bespoke">High Rise Developer</option>
                  </select>
                </label>
                <label>
                  <span>Company</span>
                  <input name="org" />
                </label>
              </Tab.Panel>
            </Tab.Panels>
            <footer className="text-sm/4 text-gray-500">
              <p className="mx-4 mb-8">
                <small>
                  You will receive a email with an unique access code to be
                  confirmed on the next screen to in order to{" "}
                  {updateEmail ? "change your email address" : "log in"}.
                </small>
              </p>
              <button className="button mx-0 flex w-full items-center justify-center gap-4 rounded-lg bg-black">
                <span className="font-light uppercase tracking-widest">
                  Submit
                </span>
                <LockClosedIcon className="-mr-4 size-4" />
              </button>
            </footer>
          </Tab.Group>
        </>
      )}
    </Modal>
  );
}
